import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Armchair } from 'lucide-react';
import {
  FloorplanAssignment,
  FloorplanEmployee,
  FloorplanLevel,
} from '../../types/floorplan';
import { BarList, EmptyState, Meter, SpecGrid, TableCard } from '../ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatNumber, formatPercent, formatTime, MISSING } from '../../utils/format';

interface StatsDialogProps {
  open: boolean;
  levels: FloorplanLevel[];
  /** Assignments in the version being viewed (may be a draft). */
  assignments: FloorplanAssignment[];
  /** Active employees. Desks can still be held by someone who has since left the roster. */
  roster: FloorplanEmployee[];
  /** Optional: name of the seating version these numbers describe. */
  versionName?: string | null;
  /** Optional: whether that version is the active one. `false` marks the numbers as a draft's. */
  isActiveVersion?: boolean;
  /** Optional: the active version's name, for the draft note ("People still see …"). */
  activeVersionName?: string | null;
  onClose: () => void;
}

const NO_TEAM = 'No team';

/** The teams table's title states the finding. */
const teamFinding = (teams: { name: string; total: number; assigned: number }[]) => {
  const short = teams.filter((team) => team.assigned < team.total);
  if (teams.length === 0) return 'Seating by team';
  if (short.length === 0) return 'Every team is fully seated';
  if (short.length > 1) return `${formatCount(short.length, 'team')} have people without a desk`;
  const [team] = short;
  const missing = team.total - team.assigned;
  const people = formatCount(missing, 'person', 'people');
  return team.name === NO_TEAM
    ? `${people} with no team ${missing === 1 ? 'has' : 'have'} no desk`
    : `${team.name} has ${people} without a desk`;
};

/** A titled block inside the dialog: heading-4 title that states the finding, then the figure. */
const StatsSection: React.FC<{ id: string; title: React.ReactNode; caption?: React.ReactNode; children: React.ReactNode }> = ({
  id,
  title,
  caption,
  children,
}) => (
  <Box component="section" aria-labelledby={id} sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
    <Box>
      <Typography id={id} variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
        {title}
      </Typography>
      {caption && (
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
          {caption}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const StatsDialog: React.FC<StatsDialogProps> = ({
  open,
  levels,
  assignments,
  roster,
  versionName,
  isActiveVersion,
  activeVersionName,
  onClose,
}) => {
  // Freshness for the meta line: when the dialog opened, or when the numbers last changed while open.
  const [asOf, setAsOf] = useState(() => new Date());
  useEffect(() => {
    if (open) setAsOf(new Date());
  }, [open, assignments, roster, levels]);

  const stats = useMemo(() => {
    const totalDesks = levels.reduce((sum, level) => sum + level.desks.length, 0);
    // Desk-based figures count every held desk, including desks held by someone no longer on the roster.
    const assignedCount = assignments.length;
    const emptyDesks = Math.max(0, totalDesks - assignedCount);
    const utilization = totalDesks > 0 ? Math.round((assignedCount / totalDesks) * 100) : 0;
    // People-based figures come from the roster, so an off-roster holder can't hide an unseated person.
    const totalEmployees = roster.length;
    const rosterIds = new Set(roster.map((employee) => employee.id));
    const assignedEmployeeIds = new Set(assignments.map((assignment) => assignment.employeeId));
    const seatedEmployees = roster.filter((employee) => assignedEmployeeIds.has(employee.id)).length;
    const unassignedEmployees = totalEmployees - seatedEmployees;
    const offRosterDesks = assignments.filter((assignment) => !rosterIds.has(assignment.employeeId)).length;

    const assignedDeskIdsByLevel = new Map<string, number>();
    assignments.forEach((assignment) => {
      const levelId = assignment.desk.levelId;
      assignedDeskIdsByLevel.set(levelId, (assignedDeskIdsByLevel.get(levelId) || 0) + 1);
    });

    const levelStats = levels.map((level) => {
      const levelDesks = level.desks.length;
      const levelAssigned = assignedDeskIdsByLevel.get(level.id) || 0;
      const levelUtil = levelDesks > 0 ? Math.round((levelAssigned / levelDesks) * 100) : 0;
      return { id: level.id, name: level.name, desks: levelDesks, assigned: levelAssigned, utilization: levelUtil };
    });

    const teamCounts = new Map<string, { total: number; assigned: number }>();

    roster.forEach((employee) => {
      const team = employee.teamName || NO_TEAM;
      const current = teamCounts.get(team) || { total: 0, assigned: 0 };
      current.total += 1;
      if (assignedEmployeeIds.has(employee.id)) {
        current.assigned += 1;
      }
      teamCounts.set(team, current);
    });

    // Least-seated teams first, so the teams that need attention are above the fold.
    const teamStats = Array.from(teamCounts.entries())
      .map(([name, counts]) => ({ name, ...counts, share: counts.total > 0 ? counts.assigned / counts.total : 1 }))
      .sort((left, right) =>
        left.share - right.share
        || (right.total - right.assigned) - (left.total - left.assigned)
        || right.total - left.total
        || left.name.localeCompare(right.name));
    const teamsFullySeated = teamStats.filter((team) => team.assigned === team.total).length;

    return {
      totalDesks,
      assignedCount,
      emptyDesks,
      utilization,
      totalEmployees,
      unassignedEmployees,
      offRosterDesks,
      levelStats,
      teamStats,
      teamsFullySeated,
    };
  }, [assignments, levels, roster]);

  // Floors keep their layout order (an ordinal set people know by position);
  // the title names the fullest one.
  const fullestLevel = stats.levelStats
    .filter((level) => level.desks > 0)
    .reduce<(typeof stats.levelStats)[number] | null>(
      (best, level) => (!best || level.utilization > best.utilization ? level : best),
      null
    );
  const floorTitle = stats.levelStats.length > 1 && fullestLevel
    ? `${fullestLevel.name} is the fullest at ${formatPercent(fullestLevel.utilization)}`
    : 'Occupancy by floor';

  const isDraft = isActiveVersion === false;
  const metaLine = [
    versionName ? `${versionName}${isDraft ? ' (draft)' : ''}` : null,
    formatCount(levels.length, 'floor'),
    formatCount(stats.totalDesks, 'desk'),
    `As of ${formatTime(asOf)}`,
  ].filter(Boolean).join(' · ');

  const occupancyCaption = (
    <>
      <Box component="span" sx={{ display: 'block' }}>
        {`${formatNumber(stats.assignedCount)} of ${formatCount(stats.totalDesks, 'desk')} in use`}
      </Box>
      {stats.offRosterDesks > 0 && (
        <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
          {`Includes ${formatCount(stats.offRosterDesks, 'desk')} held by someone no longer on the roster`}
        </Box>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="floorplan-stats-title">
      <DialogTitle component="div" id="floorplan-stats-header" sx={{ pb: 0.5 }}>
        <Typography id="floorplan-stats-title" variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
          Floor plan occupancy
        </Typography>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
          {metaLine}
        </Typography>
        {isDraft && (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
            {activeVersionName
              ? `Draft numbers. People still see “${activeVersionName}”.`
              : 'Draft numbers. No version is active yet.'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {stats.totalDesks === 0 ? (
          <EmptyState
            icon={<Armchair />}
            title="No desks on this layout yet"
            body="Occupancy appears here once desks are added in the layout editor."
          />
        ) : (
          <Box sx={{ display: 'grid', gap: 4, pt: 1.5 }}>
            {/* 2×2 on phones, one row of four from about 560px. */}
            <SpecGrid
              minColumnWidth={120}
              sx={{ columnGap: 3 }}
              items={[
                {
                  label: 'Occupancy',
                  value: formatPercent(stats.utilization),
                  caption: occupancyCaption,
                },
                {
                  label: 'Open desks',
                  value: formatNumber(stats.emptyDesks),
                  caption: 'Desks ready to assign',
                },
                {
                  label: 'Without a desk',
                  value: formatNumber(stats.unassignedEmployees),
                  caption: `Of ${formatCount(stats.totalEmployees, 'employee')} on the roster`,
                },
                {
                  label: 'Teams fully seated',
                  value: stats.teamStats.length > 0
                    ? `${formatNumber(stats.teamsFullySeated)} of ${formatNumber(stats.teamStats.length)}`
                    : MISSING,
                  caption: 'Everyone on the team has a desk',
                },
              ]}
            />

            {/* Stacked, so floor names have the full width instead of truncating in a half column. */}
            <Box sx={{ display: 'grid', gap: 4 }}>
              <StatsSection id="floorplan-stats-floors" title={floorTitle} caption="Share of each floor’s desks that are assigned">
                <BarList
                  max={100}
                  items={stats.levelStats.map((level) => ({
                    key: level.id,
                    label: level.name,
                    caption: `${formatNumber(level.assigned)} of ${formatCount(level.desks, 'desk')}`,
                    value: level.utilization,
                    display: formatPercent(level.utilization),
                    color: sbg.chart.series[0],
                  }))}
                />
              </StatsSection>

              <StatsSection
                id="floorplan-stats-teams"
                title={teamFinding(stats.teamStats)}
                caption="Seated share by team, least seated first"
              >
                {stats.teamStats.length === 0 ? (
                  <EmptyState compact title="No one on the roster" body="Teams appear here once employees are added." />
                ) : (
                  <TableCard maxHeight={320} aria-label="Seating by team">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Team</TableCell>
                          <TableCell align="right">Seated</TableCell>
                          <TableCell sx={{ width: '34%' }}>
                            <span className="sbg-sr-only">Share seated</span>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.teamStats.map((team) => {
                          const share = Math.round(team.share * 100);
                          return (
                            <TableRow key={team.name}>
                              <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={team.name}>
                                {team.name}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {`${formatNumber(team.assigned)} of ${formatNumber(team.total)}`}
                              </TableCell>
                              <TableCell>
                                <Meter
                                  value={team.assigned}
                                  max={team.total}
                                  aria-label={`${team.name}: ${formatPercent(share)} seated`}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableCard>
                )}
              </StatsSection>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatsDialog;
